// Typed client over the ac-portal Worker. The portal SPA is served SAME-ORIGIN as
// its API on each tenant's portal host, so the base URL defaults to "" and every
// request sends `credentials: "include"` — the worker sets an httpOnly `ac_portal`
// cookie on /auth/verify and reads it on the protected routes. Endpoints + payload
// shapes mirror `workers/ac-portal/src/index.ts` exactly.
//
// Auth is cookie-based: there is no token to attach. A 401 means "not signed in"
// (or session expired) and is surfaced via ApiError.status so callers route to the
// sign-in page.

import type {
  KbArticleResponse,
  KbResponse,
  RequestLinkResponse,
  SettingsResponse,
  TicketDetailResponse,
  TicketResponse,
  TicketsResponse,
  VerifyResponse,
} from "./types";

/** Same-origin by default; override with VITE_API_BASE for cross-origin dev/staging. */
export const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

/** Staging/dev only: the SPA (pages.dev) and the worker (workers.dev) live on
 *  different hosts, so the shared staging worker can't resolve a tenant from its
 *  own host. When set, every request carries X-AC-Tenant-Host so the worker
 *  targets the tenant by its real host (honored only under ALLOW_HOST_OVERRIDE).
 *  Unset in production, where the host already is the tenant. */
const TENANT_HOST = (import.meta.env.VITE_TENANT_HOST ?? "").trim();

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (TENANT_HOST) headers["X-AC-Tenant-Host"] = TENANT_HOST;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? "GET",
      headers,
      // Send + receive the httpOnly ac_portal cookie on every call.
      credentials: "include",
      ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(0, "Network error reaching the portal. Please try again.");
  }

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const message =
      (parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : undefined) ?? `Request failed (${res.status} ${res.statusText})`;
    throw new ApiError(res.status, message, parsed);
  }

  return parsed as T;
}

// ----- branding / settings (public) -----------------------------------------

export function getSettings(signal?: AbortSignal): Promise<SettingsResponse> {
  return request<SettingsResponse>("/settings", { ...(signal ? { signal } : {}) });
}

// ----- magic-link auth -------------------------------------------------------

/** POST /auth/request — emails a magic link. The token is never returned in
 *  production; on staging (ALLOW_HOST_OVERRIDE) the worker returns `dev_token`
 *  so the flow can complete without an email pipeline. */
export function requestMagicLink(email: string): Promise<RequestLinkResponse> {
  return request<RequestLinkResponse>("/auth/request", { method: "POST", body: { email } });
}

/** POST /auth/verify — consumes the token from the emailed link; sets the cookie. */
export function verifyMagicLink(token: string): Promise<VerifyResponse> {
  return request<VerifyResponse>("/auth/verify", { method: "POST", body: { token } });
}

// ----- tickets (cookie-authenticated; 401 if signed out) ---------------------

export function listTickets(signal?: AbortSignal): Promise<TicketsResponse> {
  return request<TicketsResponse>("/tickets", { ...(signal ? { signal } : {}) });
}

/** GET /tickets/:id — the bare ticket row PLUS its public conversation
 *  (internal notes are excluded server-side). */
export function getTicket(id: string, signal?: AbortSignal): Promise<TicketDetailResponse> {
  return request<TicketDetailResponse>(`/tickets/${encodeURIComponent(id)}`, {
    ...(signal ? { signal } : {}),
  });
}

export function submitTicket(input: {
  subject: string;
  body: string;
  /** The portal serves all campus user types — who is asking, and for which office. */
  affiliation?: string;
  department?: string;
  location?: string;
  phone?: string;
}): Promise<TicketResponse> {
  return request<TicketResponse>("/tickets", { method: "POST", body: input });
}

/** Public: configured departments (code + name) for the "Which office?" select. */
export function getDepartments(signal?: AbortSignal): Promise<{ departments: { code: string; name: string }[] }> {
  return request<{ departments: { code: string; name: string }[] }>("/departments", {
    ...(signal ? { signal } : {}),
  });
}

// ----- knowledge base (public) ----------------------------------------------

export function getKb(categorySlug?: string, signal?: AbortSignal): Promise<KbResponse> {
  const qs = categorySlug ? `?category=${encodeURIComponent(categorySlug)}` : "";
  return request<KbResponse>(`/kb${qs}`, { ...(signal ? { signal } : {}) });
}

export function getArticle(slug: string, signal?: AbortSignal): Promise<KbArticleResponse> {
  return request<KbArticleResponse>(`/kb/${encodeURIComponent(slug)}`, {
    ...(signal ? { signal } : {}),
  });
}
